import { useCallback, useEffect, useRef, useState } from 'react';
import { toggleId, togglePageIds } from './rowSelection';

export { runBulk, summarizeBulk } from './rowSelection';

// Selection resets when `resetKey` changes (page, search, or sort), so
// "select all" always means the rows currently on screen.
export const useRowSelection = (resetKey = '') => {
  const [selected, setSelected] = useState(() => new Set());
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setSelected(new Set());
  }

  return {
    selected,
    count: selected.size,
    isSelected: (id) => selected.has(id),
    toggle: (id) => setSelected((prev) => toggleId(prev, id)),
    togglePage: (ids) => setSelected((prev) => togglePageIds(prev, ids)),
    clear: () => setSelected(new Set())
  };
};

export const useBulkAction = (onFinished) => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const ask = (next) => {
    setError(null);
    setPending(next);
  };

  const cancel = () => {
    if (busy) return;
    setPending(null);
    setError(null);
  };

  const confirm = async () => {
    try {
      setBusy(true);
      setError(null);
      const result = await pending.run();
      const message = pending.summarize ? pending.summarize(result) : null;
      setPending(null);
      finishedRef.current?.(result, message);
    } catch (err) {
      setError(err?.message || 'That action did not finish.');
    } finally {
      setBusy(false);
    }
  };

  return { ask, pending, busy, error, confirm, cancel };
};

// Search and filter boxes refetch on every keystroke otherwise
export const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

// Each call to begin() supersedes the last, so a slow response for an old
// search or page cannot overwrite a newer one: check isCurrent() before
// setting state.
export const useLatestRequest = () => {
  const latest = useRef(0);
  return useCallback(() => {
    const id = ++latest.current;
    return { isCurrent: () => id === latest.current };
  }, []);
};

// True only until the first load finishes. Pages show the full-page spinner
// for that, and afterwards keep the table and its filters mounted while
// reloading, so a search box does not lose focus when a search finds nothing.
export const useFirstLoad = (loading) => {
  const [done, setDone] = useState(false);
  if (!loading && !done) setDone(true);
  return !done;
};
